// Table view state that survives a BACK navigation, the way scroll position
// already does.
//
// A detail screen is its own route, so opening one UNMOUNTS the list behind it
// and every `useState` in that list dies with it: the column the operator sorted
// by, the values they ticked in a filter popover, and how far they had grown a
// "Show more" window. The router restores their SCROLL position on the way back
// (`scrollRestoration`), which on its own lands them at a pixel offset in a table
// that is now sorted differently and 100 rows long, so the restore has nothing
// left to restore onto. Both halves have to come back or neither is worth much.
//
// The state is cached per HISTORY ENTRY, keyed the same way the router keys its
// own scroll cache (`location.state.__TSR_key`). Per ENTRY and not per URL is
// the whole point: coming BACK to a list is a return to the same entry and
// restores, while following a fresh link to that same list is a new entry and
// starts clean. That is what a browser does with a scroll position, so it is
// what an operator already expects from everything else on the screen.
//
// This package must not depend on a router (it is env-agnostic and `web` /
// `seller` do not use these tables), so the entry key is SUPPLIED by the app
// through `ViewStateEntryContext`. An app that provides nothing gets the empty
// default, which turns restoration off and leaves every hook behaving exactly as
// it did before this file existed.
import { createContext, useContext, useEffect, useState } from "react";

/**
 * The current history entry, as the app's router names it. Empty means "this app
 * does not restore view state", which is the default for every app that never
 * provides it.
 */
export const ViewStateEntryContext = createContext<string>("");

/** How many slots to keep. One slot is one table's one piece of state on one
 *  history entry, so a busy session holds a few dozen; the cap only exists so a
 *  session that never reloads cannot grow forever. Eviction is
 *  least-recently-written, which is the oldest history entries, and losing those
 *  degrades to today's behavior (the view rebuilds) rather than to a wrong view. */
const MAX_SLOTS = 200;

/** Slot → last known value. `unknown` because one cache serves every table; each
 *  slot's shape is whatever its single owning call site puts there, which is what
 *  makes restore keys having to be unique a real requirement and not style. */
const slots = new Map<string, unknown>();

/** The cache key for one piece of state on one history entry. Both halves are
 *  needed: the entry alone would collide across every table on a page, and the
 *  restore key alone would leak one entry's view onto another.
 *
 *  Length-prefixed rather than joined on a delimiter. A separator has to be a
 *  character neither half can contain, and the entry key is not ours to
 *  constrain: it is whatever the router stamped, falling back to a full HREF,
 *  which can contain anything. The prefix makes the pair unambiguous without
 *  needing that promise. */
export function viewStateSlotKey(entryKey: string, restoreKey: string): string {
  return `${entryKey.length}:${entryKey}:${restoreKey}`;
}

/** A hit carries the value; a miss says so rather than returning a blank, because
 *  a stored value is legitimately allowed to be `null` (an unsorted table) and a
 *  caller must be able to tell that apart from having never stored anything. */
export type ViewSlotRead<T> = { readonly hit: true; readonly value: T } | { readonly hit: false };

export function readViewSlot<T>(slot: string): ViewSlotRead<T> {
  if (!slots.has(slot)) return { hit: false };
  // Unchecked by construction: see `slots` above. One restore key, one call site,
  // one shape.
  return { hit: true, value: slots.get(slot) as T };
}

export function writeViewSlot(slot: string, value: unknown): void {
  // Delete before set so the Map's insertion order stays recency order, which is
  // what makes the eviction below drop the oldest entries rather than arbitrary
  // ones.
  slots.delete(slot);
  slots.set(slot, value);
  while (slots.size > MAX_SLOTS) {
    const oldest = slots.keys().next();
    if (oldest.done) break;
    slots.delete(oldest.value);
  }
}

/** Drop everything. For tests, which must not leak one case's slots into the next. */
export function resetViewStateCache(): void {
  slots.clear();
}

/**
 * `useState`, except the value comes back after a history BACK hop.
 *
 * Pass the `restoreKey` a call site owns (unique across the app) to opt in;
 * `undefined` opts out and this is a plain `useState`. `initial` takes a value or
 * a lazy initializer, same as `useState`. Restoration happens on
 * MOUNT only, and the value is then carried onto each new history entry the
 * component lives through, which together give the two behaviors that matter:
 *
 *  - Editing a search param pushes a new entry while the list stays MOUNTED, so
 *    the view must not reset under the operator mid-edit. It does not: React
 *    keeps the state and this hook simply records it against the new entry too.
 *    Skipping that record is the bug it looks like you can skip - the entry the
 *    operator eventually comes BACK to is the one they left, not the one they
 *    arrived on.
 *  - Opening a detail screen unmounts the list, and coming back mounts a fresh
 *    one on the entry it left, which is the read that restores.
 */
export function useRestoredState<T>(
  restoreKey: string | undefined,
  initial: T | (() => T),
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const entryKey = useContext(ViewStateEntryContext);
  // Both halves have to be present: an app with no provider, or a call site that
  // named no key, is opted out.
  const slot = entryKey && restoreKey ? viewStateSlotKey(entryKey, restoreKey) : null;

  const [value, setValue] = useState<T>(() => {
    if (slot !== null) {
      const found = readViewSlot<T>(slot);
      if (found.hit) return found.value;
    }
    // Lazy initializers are resolved the way `useState` resolves them, so this
    // stays a drop-in for it. A restored slot skips the initializer entirely,
    // which is the point for a default computed off the clock: "the last 30 days"
    // must not be recomputed over a range the reader had already changed.
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });

  useEffect(() => {
    if (slot === null) return;
    writeViewSlot(slot, value);
  }, [slot, value]);

  return [value, setValue];
}
